import SaporeApiClient from "./saporeApiClient";
import BearerAuthorizationManager from "./BearerAuthorizationManager";
import SaporeRemoteRequester from "./SaporeRemoteRequester";
import FirebaseAuthenticationMethod from "./FirebaseAuthenticationMethod";
import FakeAuthenticationMethod from "./FakeAuthenticationMethod";
import Session from "./Session";
import Business from './business'
import {
    ASSIGN_PERMISSIONS_PATH,
    ASSIGN_ROLES_USER_PATH, AUTHORIZATION_PATH, AUTHORIZATIONS_PATH,
    BACKOFFICE_USERS_PATH, CREATE_BACKOFFICE_USER_PATH,
    CREATE_ORDER_PATH, CREATE_USER_PATH,
    CURRENT_ACCOUNTS_PATH,
    SUPPORT_PATH, ORDER_LOG_PATH, ORDER_PATH,
    ORDERS_PATH,
    PURCHASE_ORDERS_PATH,
    USERS_PATH, VIEW_AUTHORIZATION_PATH, VIEW_CURRENT_ACCOUNT_PATH,
    CUSTOMER_ONBOARDINGS_PATH, CUSTOMER_ONBOARDING_EDITION_PATH,
    CREATE_CUSTOMER_ONBOARDING_PATH,
    DASHBOARD_PATH,
    CREATE_ALLOWANCE_PATH,
    ALLOWANCES_PATH,
    CUSTOMERS_PATH, CUSTOMER_PATH,
    DELIVERY_SITE_ONBOARDING_CREATE_PATH, DELIVERY_SITE_ONBOARDING_EDIT_PATH,
    DELIVERY_SITE_PATH, BUDGET_PATH, LOGISTICA_PATH,
    SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PATH, MARKETPLACE_DEMO_PATH, CREATE_LEAD_PATH, LEAD_ASSIGNMENT_QUEUE_PATH,
    LEAD_PIPELINE_PATH,
} from "./routes";

export class ApplicationFactory {
    async create() {
        const authenticationMethod = this._authenticationMethod();
        const session = new Session(authenticationMethod)
        let application = new Application(authenticationMethod, session);
        await application.setUpApiClient();
        authenticationMethod.withApplication(application)
        await authenticationMethod.subscribeToAuthEvents();
        session.withApplication(application);

        if (session.active()) {
            try {
                await session.refreshUserPermissions();
            } catch (error) {
                console.warn('Failed to refresh user permissions on page load:', error);
            }
        }

        return application;
    }

    _authenticationMethod() {
        const fakeAuthenticationIsEnabled = process.env.REACT_APP_AUTHENTICATION_METHOD === 'FAKE';
        return fakeAuthenticationIsEnabled ? new FakeAuthenticationMethod() : new FirebaseAuthenticationMethod();
    }
}

export class Application {
    constructor(authenticationMethod, session) {
        this._business = new Business()
        this._authenticationMethod = authenticationMethod
        this._session = session
        this._saporeApiClient = null;
    }

    async setUpApiClient() {
        const requester = await this._setUpRequester();
        this._saporeApiClient = new SaporeApiClient(requester, this._authenticationMethod, this);
    }

    async _setUpRequester() {
        const bearerAuthorizationManager = new BearerAuthorizationManager(this._session);
        const url = await this._getBackendUrl();
        return new SaporeRemoteRequester(url, bearerAuthorizationManager);
    }

    session() {
        return this._session
    }

    authenticationMethod() {
        return this._authenticationMethod
    }

    apiClient() {
        return this._saporeApiClient
    }

    business() {
        return this._business
    }

    async _getBackendUrl() {
        const backendUrl = process.env.REACT_APP_BACKEND_URL
        if (backendUrl) {
            return backendUrl
        }
        const response = await fetch('/api')
        if (!response.ok) {
            throw new Error('Failed to fetch API URL');
        }
        const json = await response.json();
        return json["API_URL"];
    }

    googleMapsApiKey() {
        return process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    }

    routePermissionMap() {
        return {
            [SUPPORT_PATH]: ['SUPPORT_PERMISSION'],
            [BUDGET_PATH]: ['BUDGET_PERMISSION'],
            [LOGISTICA_PATH]: ['LOGISTICS_SYSTEM_PERMISSION'],
            [MARKETPLACE_DEMO_PATH]: ['DEMO_PERMISSION'],
            [ORDERS_PATH]: ['ORDER_PERMISSION'],
            [ORDER_PATH]: ['ORDER_PERMISSION'],
            [ORDER_LOG_PATH]: ['ORDER_PERMISSION'],
            [CREATE_ORDER_PATH]: ['CREATE_ORDER_PERMISSION'],
            [PURCHASE_ORDERS_PATH]: ['PURCHASE_ORDER_PERMISSION'],
            [CURRENT_ACCOUNTS_PATH]: ['CURRENT_ACCOUNT_PERMISSION'],
            [VIEW_CURRENT_ACCOUNT_PATH]: ['CURRENT_ACCOUNT_PERMISSION'],
            [USERS_PATH]: ['USER_PERMISSION'],
            [CREATE_USER_PATH]: ['USER_PERMISSION'],
            [ASSIGN_ROLES_USER_PATH]: ['USER_PERMISSION'],
            [BACKOFFICE_USERS_PATH]: ['BACKOFFICE_USER_PERMISSION'],
            [CREATE_BACKOFFICE_USER_PATH]: ['BACKOFFICE_USER_PERMISSION'],
            [ASSIGN_PERMISSIONS_PATH]: ['BACKOFFICE_USER_PERMISSION'],
            [AUTHORIZATIONS_PATH]: ['AUTHORIZATION_PERMISSION'],
            [AUTHORIZATION_PATH]: ['AUTHORIZATION_PERMISSION'],
            [VIEW_AUTHORIZATION_PATH]: ['AUTHORIZATION_PERMISSION'],
            [CUSTOMER_ONBOARDINGS_PATH]: ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'LOGISTICS_PERMISSION', 'TAX_PERMISSION', 'FINANCE_PERMISSION',
                'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            [CUSTOMER_ONBOARDING_EDITION_PATH]: ['CLOSER_PERMISSION', 'COMMERCIAL_PERMISSION',
                'LOGISTICS_PERMISSION', 'TAX_PERMISSION', 'FINANCE_PERMISSION',
                'COMMERCIAL_AUTHORIZATION_PERMISSION', 'LOGISTICS_AUTHORIZATION_PERMISSION',
                'TAX_AUTHORIZATION_PERMISSION', 'FINANCE_AUTHORIZATION_PERMISSION',
                'ONBOARDINGS_ERP_IMPORTER_PERMISSION'],
            [CREATE_CUSTOMER_ONBOARDING_PATH]: ['CLOSER_PERMISSION'],
            [CUSTOMERS_PATH]: ['ORDER_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION', 'CLOSER_PERMISSION'],
            [CUSTOMER_PATH]: ['ORDER_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION', 'CLOSER_PERMISSION'],
            [DASHBOARD_PATH]: ['DASHBOARD_PERMISSION'],
            [DELIVERY_SITE_ONBOARDING_CREATE_PATH]: ['DELIVERY_SITE_ONBOARDING_PERMISSION', 'CLOSER_PERMISSION'],
            [DELIVERY_SITE_ONBOARDING_EDIT_PATH]: ['DELIVERY_SITE_ONBOARDING_PERMISSION', 'ONBOARDINGS_ERP_IMPORTER_PERMISSION', 'CLOSER_PERMISSION'],
            [DELIVERY_SITE_PATH]: ['ORDER_PERMISSION'],
            [CREATE_ALLOWANCE_PATH]: ['COMMERCIAL_PERMISSION'],
            [ALLOWANCES_PATH]: ['COMMERCIAL_PERMISSION', 'ALLOWANCES_CREDIT_NOTES_PERMISSION'],
            [SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PATH]: ['SOLD_BOXES_BY_CUSTOMER_AND_ARTICLE_PERMISSION'],
            [CREATE_LEAD_PATH]: ['CLOSER_PERMISSION', 'CUSTOMER_SERVICE_AGENT_PERMISSION',
                'HORECA_CHANNEL_HEAD_PERMISSION', 'RETAIL_CHANNEL_HEAD_PERMISSION',
                'CHAINS_CHANNEL_HEAD_PERMISSION', 'COMMERCIAL_MANAGER_PERMISSION'],
            [LEAD_ASSIGNMENT_QUEUE_PATH]: ['HORECA_CHANNEL_HEAD_PERMISSION', 'COMMERCIAL_MANAGER_PERMISSION'],
            [LEAD_PIPELINE_PATH]: ['CLOSER_PERMISSION', 'HORECA_CHANNEL_HEAD_PERMISSION',
                'RETAIL_CHANNEL_HEAD_PERMISSION', 'CHAINS_CHANNEL_HEAD_PERMISSION',
                'COMMERCIAL_MANAGER_PERMISSION'],
        }
    }

    canAccessPath(path) {
        if (this.hasSuperuserPermission()) return true;
        const requiredPermissions = this.routePermissionMap()[path]
        const userPermissions = this.permissions()
        return requiredPermissions.some(requiredPermission => userPermissions.includes(requiredPermission))
    }

    permissions() {
        return this.session().permissions()
    }

    recoverPassword(email, responseHandler) {
        return this._saporeApiClient.recoverPassword(email, responseHandler);
    }

    hasERPSupportPermission(){
        return this.permissions().includes('ERP_SUPPORT_PERMISSION')
    }

    hasSuperuserPermission() {
        return this.permissions().includes('SUPERUSER_PERMISSION')
    }

    hasEDISupportPermission(){
        return this.permissions().includes('EDI_SUPPORT_PERMISSION')
    }

    hasOnboardingERPImporterPermission(){
        return this.permissions().includes('ONBOARDINGS_ERP_IMPORTER_PERMISSION')
    }

    hasCurrentAccountPermission(){
        return this.permissions().includes('CURRENT_ACCOUNT_SUPPORT_PERMISSION')
    }

    hasGeneralSupportPermission(){
        return this.permissions().includes('GENERAL_SUPPORT_PERMISSION')
    }

    hasCloserPermission(){
        return this.permissions().includes('CLOSER_PERMISSION')
    }

    hasDeliverySiteOnboardingPermission(){
        return this.permissions().includes('DELIVERY_SITE_ONBOARDING_PERMISSION')
    }

    hasAllowancesCreditNotesPermission(){
        return this.permissions().includes('ALLOWANCES_CREDIT_NOTES_PERMISSION')
    }

    hasPaymentReminderPermission(){
        return this.permissions().includes('PAYMENT_REMINDER_PERMISSION')
    }

    hasHighRiskClassificationPermission(){
        return this.permissions().includes('HIGH_RISK_CLASSIFICATION_PERMISSION')
    }

    canManageDeliverySiteOnboardings(){
        return this.hasDeliverySiteOnboardingPermission() || this.hasCloserPermission()
    }
}
